import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, IconButton, Typography, CircularProgress, Alert,
  Grid, Table, TableHead, TableRow, TableCell, TableBody, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory, useRouteMatch } from 'react-router-dom';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DetailRow } from '../../components/InvField';
import {
  getPurchaseOrder, getPurchaseOrderItems, submitPoForApproval,
  approvePo, rejectPo, sendPoToVendor, cancelPo, closePo,
  deletePurchaseOrder,
} from '../../api/procurement';
import { getGrns } from '../../api/grn';
import useItemNames from '../../hooks/useItemNames';
import useVendorLookup from '../../hooks/useVendorLookup';
import { formatDate, formatCurrency, lineTotal } from '../../utils';
import { IPD_COLORS } from '../../theme';
import { toast } from 'react-toastify';
import { derivePoLifecycle, getPoAllowedActions, getPoActionBlockers, getPoDisplayStatus, hasDisplayValue } from '../../utils/poLifecycle';
import useInvRole from '../../hooks/useInvRole';
import { filterPoActionsByRole, PO_DIALOG_ACTION_PERMISSION } from '../../utils/inventoryRoles';

export default function PoDetailDrawer({ poId, open, onClose, onRefreshed }) {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { can } = useInvRole();
  const { resolveItem } = useItemNames();
  const { resolveVendorName } = useVendorLookup();
  const [po, setPo] = useState(null);
  const [poItems, setPoItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [grns, setGrns] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [actioning, setActioning] = useState(false);

  async function load() {
    if (!poId) return;
    setLoading(true); setError(null);
    try {
      // API: GET /inventory/purchase-orders/{id}/
      const p = await getPurchaseOrder(poId);
      setPo(p);
      const scope = { tenant_id: p.tenant_id, hospital_id: p.hospital_id };
      const [items, grnData] = await Promise.all([
        getPurchaseOrderItems(poId, scope).catch(() => []),
        getGrns({ purchase_order_id: poId }).catch(() => []),
      ]);
      setPoItems(Array.isArray(items) ? items : (items?.data || items?.items || []));
      setGrns(Array.isArray(grnData) ? grnData : (grnData?.data || grnData?.items || []));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => {
    if (open && poId) { setPo(null); setPoItems([]); setGrns([]); load(); }
  }, [poId, open]);

  async function executeAction() {
    const required = PO_DIALOG_ACTION_PERMISSION[dialog.action];
    if (required && !can(required)) return;
    // The dialog vocabulary differs from the lifecycle keys ('short-close' vs 'short_close').
    const blocked = getPoActionBlockers(po, poItems, grns)[dialog.action.replace('-', '_')];
    if (blocked) { toast.error(blocked); setDialog(null); return; }
    setActioning(true);
    try {
      const scope = { tenant_id: po?.tenant_id, hospital_id: po?.hospital_id };
      const remarks = reason || undefined;
      // API: POST /inventory/purchase-orders/?action=submit-for-approval
      if (dialog.action === 'submit') await submitPoForApproval(poId, { ...scope, remarks });
      // API: POST /inventory/purchase-orders/?action=approve
      else if (dialog.action === 'approve') await approvePo(poId, { ...scope, remarks });
      // API: POST /inventory/purchase-orders/?action=reject
      else if (dialog.action === 'reject') await rejectPo(poId, { ...scope, reason, remarks });
      // API: POST /inventory/purchase-orders/?action=send-to-vendor
      else if (dialog.action === 'send') await sendPoToVendor(poId, { ...scope, remarks });
      // API: POST /inventory/purchase-orders/?action=cancel
      else if (dialog.action === 'cancel') await cancelPo(poId, { ...scope, reason, remarks });
      // API: POST /inventory/purchase-orders/?action=close
      else if (dialog.action === 'close') await closePo(poId, { ...scope, remarks });
      // API: POST /inventory/purchase-orders/{id}/close/ with force_close
      else if (dialog.action === 'short-close') await closePo(poId, { ...scope, reason, force_close: true });
      // API: DELETE /inventory/purchase-orders/{id}/
      else if (dialog.action === 'delete') {
        await deletePurchaseOrder(poId, scope);
        setDialog(null);
        setReason('');
        onRefreshed?.();
        onClose();
        toast.success('Purchase order deleted');
        return;
      }
      setDialog(null); setReason('');
      await load();
      onRefreshed?.();
      if (dialog.action === 'approve') toast.success('Purchase order approved');
      else if (dialog.action === 'reject') toast.success('Purchase order rejected');
    } catch (e) {
      toast.error(e?.message || 'Action failed');
    } finally { setActioning(false); }
  }

  const grandTotal = poItems.reduce(
    (sum, l) => sum + lineTotal(l.ordered_quantity, l.unit_rate, l.tax_percent, l.discount_percent), 0
  );

  const lifecycle = derivePoLifecycle(po, poItems, grns);
  const allowedActions = filterPoActionsByRole(getPoAllowedActions(po, poItems, grns), can);
  const blockers = getPoActionBlockers(po, poItems, grns);
  const displayStatus = getPoDisplayStatus(po, poItems, grns);
  const actionButtons = [];
  if (po) {
    if (allowedActions.has('submit')) actionButtons.push(
      <InvButton key="submit" size="small" tone="mint"
        onClick={() => setDialog({ action: 'submit', title: 'Submit for Approval', desc: 'Submit PO for approval?' })}>
        Submit
      </InvButton>
    );
    if (allowedActions.has('delete')) actionButtons.push(
      <InvButton key="delete" size="small" tone="red" variant="outlined"
        onClick={() => setDialog({
          action: 'delete',
          title: 'Delete Purchase Order',
          desc: 'Delete this purchase order? It will be deactivated and removed from the list.',
          destructive: true,
        })}>
        Delete
      </InvButton>
    );
    if (allowedActions.has('approve') || allowedActions.has('reject')) actionButtons.push(
      allowedActions.has('approve') ? (
        <InvButton key="approve" size="small"
          onClick={() => setDialog({ action: 'approve', title: 'Approve PO', desc: 'Approve this purchase order?' })}>
          Approve
        </InvButton>
      ) : null,
      allowedActions.has('reject') ? (
        <InvButton key="reject" size="small" tone="red" variant="outlined"
          onClick={() => setDialog({ action: 'reject', title: 'Reject PO', desc: 'Reject this PO?', destructive: true, requireReason: true })}>
          Reject
        </InvButton>
      ) : null
    );
    if (allowedActions.has('send_to_vendor')) actionButtons.push(
      <InvButton key="send" size="small" tone="mint"
        onClick={() => setDialog({ action: 'send', title: 'Send to Vendor', desc: 'Send this PO to the vendor?' })}>
        Send to Vendor
      </InvButton>
    );
    if (allowedActions.has('create_grn')) actionButtons.push(
      <InvButton key="grn" size="small" variant="outlined" tone="teal"
        onClick={() => history.push(`${url.replace('/pos', '')}/grn/new?po=${poId}`)}>
        Create GRN
      </InvButton>
    );
    if (allowedActions.has('close')) actionButtons.push(
      <InvButton key="close" size="small" tone="mint" variant="outlined"
        onClick={() => setDialog({
          action: 'close', title: 'Close PO',
          desc: 'All items have been fully received. Close this purchase order?',
        })}>
        Close
      </InvButton>
    );
    if (allowedActions.has('short_close')) actionButtons.push(
      <Tooltip key="short-close" title={blockers.short_close || ''}>
        <span>
          <InvButton size="small" tone="red" variant="outlined"
            disabled={Boolean(blockers.short_close)}
            onClick={() => setDialog({
              action: 'short-close',
              title: 'Short Close PO',
              desc: 'Some items still have pending quantity. Short-closing writes off the remaining balance without waiting for further delivery — a reason is required.',
              destructive: true,
              requireReason: true,
            })}>
            Short Close
          </InvButton>
        </span>
      </Tooltip>
    );
    if (allowedActions.has('cancel')) actionButtons.push(
      <Tooltip key="cancel" title={blockers.cancel || ''}>
        <span>
          <InvButton size="small" tone="red" variant="outlined"
            disabled={Boolean(blockers.cancel)}
            onClick={() => setDialog({
              action: 'cancel',
              title: lifecycle.effectiveStatus === 'PARTIALLY_RECEIVED' ? 'Cancel Remaining Quantity' : 'Cancel PO',
              desc: lifecycle.effectiveStatus === 'PARTIALLY_RECEIVED'
                ? 'Only the remaining quantity can be cancelled. Continue?'
                : 'Cancel this PO?',
              destructive: true,
              requireReason: true,
            })}>
            {lifecycle.effectiveStatus === 'PARTIALLY_RECEIVED' ? 'Cancel Remaining' : 'Cancel'}
          </InvButton>
        </span>
      </Tooltip>
    );
    if (allowedActions.has('audit')) actionButtons.push(
      <InvButton key="audit" size="small" variant="outlined" tone="teal"
        onClick={() => toast.info('Audit history is available from backend logs for this PO.')}>
        Audit History
      </InvButton>
    );
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 600, md: 700 },
            bgcolor: IPD_COLORS.pageBg,
            borderLeft: `1px solid ${IPD_COLORS.border}`,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 2,
          borderBottom: `1px solid ${IPD_COLORS.border}`,
          bgcolor: IPD_COLORS.cardBg,
          flexShrink: 0,
        }}>
          <Box>
            <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
              Material Management · Purchase Order
            </Typography>
            <Typography variant="h6" sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
              {po?.po_number || '—'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {actionButtons}
            <IconButton onClick={onClose} size="small" sx={{ color: IPD_COLORS.textSecondary, ml: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
              <CircularProgress sx={{ color: IPD_COLORS.primary }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          ) : po ? (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <SectionCard title="PO Details" actions={
                  displayStatus ? <StatusChip status={displayStatus} /> : null
                }>
                  <DetailRow label="Vendor">{resolveVendorName(po.vendor_id)}</DetailRow>
                  <DetailRow label="Expected">{formatDate(po.expected_delivery_date)}</DetailRow>
                  {hasDisplayValue(po.payment_terms) && (
                    <DetailRow label="Payment Terms">{po.payment_terms}</DetailRow>
                  )}
                  {hasDisplayValue(po.delivery_address) && (
                    <DetailRow label="Delivery Address">{po.delivery_address}</DetailRow>
                  )}
                  {hasDisplayValue(po.remarks) && (
                    <DetailRow label="Remarks">{po.remarks}</DetailRow>
                  )}
                  <DetailRow label="Created">{formatDate(po.created_at)}</DetailRow>
                </SectionCard>
              </Grid>
              <Grid item xs={12}>
                <SectionCard title="Line Items">
                  <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                        <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                        <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Rate</TableCell>
                        <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Tax%</TableCell>
                        <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Disc%</TableCell>
                        <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {poItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ color: IPD_COLORS.textSecondary, py: 3 }}>
                            No line items
                          </TableCell>
                        </TableRow>
                      ) : poItems.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>{resolveItem(item)}</TableCell>
                          <TableCell align="right">{item.ordered_quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_rate)}</TableCell>
                          <TableCell align="right">{item.tax_percent || 0}%</TableCell>
                          <TableCell align="right">{item.discount_percent || 0}%</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(lineTotal(item.ordered_quantity, item.unit_rate, item.tax_percent, item.discount_percent))}
                          </TableCell>
                        </TableRow>
                      ))}
                      {poItems.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="right"
                            sx={{ fontWeight: 700, color: IPD_COLORS.primary, borderTop: `2px solid ${IPD_COLORS.border}` }}>
                            Grand Total
                          </TableCell>
                          <TableCell align="right"
                            sx={{ fontWeight: 700, color: IPD_COLORS.primary, borderTop: `2px solid ${IPD_COLORS.border}` }}>
                            {formatCurrency(grandTotal)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </SectionCard>
              </Grid>
            </Grid>
          ) : null}
        </Box>
      </Drawer>

      <ConfirmDialog
        open={Boolean(dialog)} title={dialog?.title} description={dialog?.desc}
        destructive={dialog?.destructive} requireReason={dialog?.requireReason}
        reasonValue={reason} onReasonChange={setReason} loading={actioning}
        onConfirm={executeAction} onCancel={() => { setDialog(null); setReason(''); }}
      />
    </>
  );
}
